import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { seriesColor, useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { cell, humanise, isNumeric } from '../format';
import { animation, baseGrid, tooltipChrome, valueAxisX, valueAxisY } from '../options';

/**
 * Two measures against each other — the form for "is X related to Y?".
 *
 * A scatter needs two numeric columns for the axes; the first non-numeric
 * column names each point (for the tooltip). A bubble adds a third numeric
 * column mapped to the mark's area, so a third measure rides along without a
 * third axis. Both are the same builder — a bubble is a scatter whose points
 * carry a size.
 *
 * Point area, not radius, tracks the value: doubling a bubble's radius
 * quadruples the ink and lies about the magnitude, so the size column is
 * square-rooted into the radius.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const MIN_R = 5;
const MAX_R = 26;

const scatterView = (data: ChartData, theme: ReturnType<typeof useTheme>, sized: boolean) => {
  if (data.columns.length < 2 || !data.rows.length) return null;

  const numericAt = (i: number) => data.rows.slice(0, 30).some((r) => isNumeric(r[i]));
  const numeric = data.columns.map((_, i) => i).filter(numericAt);
  if (numeric.length < 2) return null;

  const xi = numeric[0];
  const yi = numeric[1];
  const si = sized && numeric.length > 2 ? numeric[2] : -1;
  const li = data.columns.findIndex((_, i) => !numericAt(i));

  const points = data.rows
    .filter((r) => isNumeric(r[xi]) && isNumeric(r[yi]))
    .map((r) => ({
      // [x, y, size] — size rides in the value array (ECharts-idiomatic) so no
      // custom field collides with the strict data-item type.
      value: [
        r[xi] as number,
        r[yi] as number,
        si >= 0 && isNumeric(r[si]) ? (r[si] as number) : 0,
      ] as [number, number, number],
      name: li >= 0 ? String(r[li] ?? '') : '',
    }));

  if (!points.length) return null;

  const maxSize = Math.max(...points.map((p) => p.value[2]), 0) || 1;
  const xName = humanise(data.columns[xi]);
  const yName = humanise(data.columns[yi]);
  const sName = si >= 0 ? humanise(data.columns[si]) : '';

  const option: EChartsOption = {
    ...animation,
    grid: baseGrid({ legend: false }),
    xAxis: valueAxisX(theme, { name: xName }),
    yAxis: valueAxisY(theme, { name: yName }),
    tooltip: {
      trigger: 'item',
      ...tooltipChrome(theme),
      formatter: (params: unknown) => {
        const p = params as { data?: { value: [number, number, number]; name: string } };
        const d = p.data;
        const root = document.createElement('div');
        root.className = 'chart-tip';
        if (d?.name) {
          const h = document.createElement('div');
          h.className = 'chart-tip__title';
          h.textContent = d.name;
          root.append(h);
        }
        const line = (name: string, value: string) => {
          const row = document.createElement('div');
          row.className = 'chart-tip__row';
          const n = document.createElement('span');
          n.className = 'chart-tip__name';
          n.textContent = name;
          const v = document.createElement('strong');
          v.className = 'chart-tip__value';
          v.textContent = value;
          row.append(n, v);
          return row;
        };
        root.append(line(xName, cell(d?.value[0], 'exact')));
        root.append(line(yName, cell(d?.value[1], 'exact')));
        if (si >= 0 && d) root.append(line(sName, cell(d.value[2], 'exact')));
        return root;
      },
    },
    series: [
      {
        type: 'scatter' as const,
        data: points,
        symbolSize: (val: unknown) => {
          const v = val as number[];
          if (si < 0) return MIN_R * 1.6;
          const t = Math.sqrt(Math.max(0, v[2] ?? 0) / maxSize);
          return MIN_R + t * (MAX_R - MIN_R);
        },
        itemStyle: {
          color: seriesColor(theme, 0),
          opacity: 0.72,
          borderColor: theme.surface,
          borderWidth: 1,
        },
        emphasis: { itemStyle: { opacity: 1 } },
      },
    ],
  };

  return { option };
};

export const ScatterPlot = ({ data }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => scatterView(data, theme, false), [data, theme]);
  if (!view) return <Empty message="Needs two numeric columns to plot" />;
  return (
    <Plot>
      <EChart option={view.option} ariaLabel="Scatter plot" />
    </Plot>
  );
};

export const BubbleChart = ({ data }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => scatterView(data, theme, true), [data, theme]);
  if (!view) return <Empty message="Needs two numeric columns to plot" />;
  return (
    <Plot>
      <EChart option={view.option} ariaLabel="Bubble chart" />
    </Plot>
  );
};
